/**
 * CheckoutStep Component
 */

import {
  completeStep,
  registerStep,
  removeCompletedStep,
  saveCheckoutStepToLocalStorage,
  setActiveStep,
} from '../../../redux/actions/checkout';
import { validateFields } from '../../../redux/form/validation';
import { outerHeight } from '../../../utils/dom';
import './checkout-step.scss';

import { reverse } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import * as Velocity from 'velocity-animate';

import * as WindowSizeListener from 'react-window-size-listener';
import Button from '../../atoms/buttons/Button';
import FormError from '../../atoms/feedback/FormError';
import { IconArrowRight, IconCheck } from '../../atoms/images/Icon';
import Heading from '../../atoms/text/Heading';
import { CheckoutStepId } from './config';

export type ValidationFunction = (values: any, dispatch: Dispatch<any>, props: any) => Promise<any>;

interface IOwnStateProps {
  isActive: boolean;
  isComplete: boolean;
}

interface IOwnDispatchProps {
  complete: () => void;
  register: () => void;
  setActive: () => void;
}

interface IOwnProps {
  customValidation?: ValidationFunction;
  defaultSelected?: boolean;
  error?: string;
  formValidations?: IFormValidations;
  handleSubmit?: any;
  id: string;
  onSelect?: () => void;
  onSubmit?: ValidationFunction;
  showNextStepButton?: boolean;
  submitting?: boolean;
  title: string;
}

type CheckoutStepProps = IOwnProps & IOwnStateProps & IOwnDispatchProps;

interface IOwnState {
  isOpen: boolean;
}

const classes = new BEM('checkout__step');

class CheckoutStep extends React.Component<CheckoutStepProps, IOwnState> {
  private content: HTMLElement;
  private contentHeight: number;
  private contentWrapper: HTMLElement;
  private isComponentMounted: boolean;

  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
    };
  }

  componentDidMount() {
    this.contentHeight = outerHeight(this.content);
    this.isComponentMounted = true;

    this.props.register();

    if (this.props.defaultSelected) {
      this.props.setActive();
    }

    if (this.props.isActive) {
      this.toggleSlide(true, 0);
    }
  }

  componentDidUpdate(prevProps: CheckoutStepProps) {
    this.onResize();

    if (prevProps.isActive !== this.props.isActive) {
      if (!this.props.isActive) {
        this.setState({ isOpen: false }, () => {
          this.toggleSlide(this.props.isActive);
        });
      } else {
        this.toggleSlide(this.props.isActive);
        if (!this.props.defaultSelected) {
          this.props.setActive();
        }
      }
    }
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
  }

  select = () => {
    this.props.setActive();

    if (this.props.onSelect) {
      this.props.onSelect();
    }
  }

  onResize = () => {
    if (this.props.isActive) {
      const contentHeight = outerHeight(this.content);
      if (contentHeight !== this.contentHeight) {
        this.contentHeight = contentHeight;
        this.contentWrapper.style.height = `${this.contentHeight}px`;
      }
    }
  }

  onSubmit: ValidationFunction = (values, dispatch, props) => {
    const {
      customValidation,
      id,
      isComplete,
    } = this.props;

    const validate: ValidationFunction = customValidation || this.validate;

    return validate(values, dispatch, props)
      .then(() => {
        return new Promise((resolve) => {
          const delay = id === CheckoutStepId.review ? 2000 : 0;
          setTimeout(() => {
            dispatch(completeStep({ id }));
            dispatch(saveCheckoutStepToLocalStorage({ stepForm: { [id]: values } }));
            resolve();
          }, delay);
        });
      }).catch((err) => {
        if (isComplete) {
          dispatch(removeCompletedStep({ id }));
        }
        throw err;
      });
  }

  validate = (values) =>
    this.props.formValidations ?
      validateFields(values, this.props.formValidations!, {
        validatePostalCode: [values.hasOwnProperty('postalCodeValidationRegExp') && values.postalCodeValidationRegExp],
      })
      : Promise.resolve()

  render() {
    const {
      error,
      id,
      isActive,
      isComplete,
      handleSubmit,
      showNextStepButton,
      submitting,
    } = this.props;

    const classModifiers = {
      [id]: true,
      'is-active': !!isActive,
      'is-complete': !!isComplete,
      'is-open': this.state.isOpen,
    };

    return (
      <section {...classes('', classModifiers)}>
        <header {...classes('header')} onClick={() => (!isActive && isComplete) && this.select()}>
          <Heading is="h3" {...classes('title')}>{this.props.title}</Heading>
          {error && <FormError error={error} />}
          {isComplete && (
            <div {...classes('check-complete')}><IconCheck /></div>
          )}
        </header>
        <div {...classes('content-wrapper')} ref={(node) => this.contentWrapper = node!}>
          <form
            ref={(node) => this.content = node!}
            onSubmit={handleSubmit && handleSubmit(this.props.onSubmit || this.onSubmit)}
            {...classes('content')}
          >
            {this.props.children}
            {showNextStepButton !== false && (
              <div {...classes('footer')}>
                <Button
                  label="Next"
                  icon={<IconArrowRight />}
                  iconPosition="right"
                  colorScheme="dark"
                  disabled={!!submitting}
                  isSubmit
                />
              </div>
            )}
          </form>
        </div>
        <WindowSizeListener onResize={this.onResize} />
      </section>
    );
  }

  private toggleSlide(toggle = true, duration = 500) {
    const height = [this.contentHeight, 0];
    if (!toggle) {
      reverse(height);
    }

    Velocity(this.contentWrapper, { height }, {
      duration,
      delay: toggle ? 500 : 0,
      easing: 'easeInOutQuint',
      complete: () => {
        if (toggle && this.isComponentMounted) {
          this.setState({ isOpen: true });
        }
      },
    });
  }
}

const ConnectedCheckoutStep = connect(
  (state: IStoreState, ownProps: IOwnProps): IOwnStateProps => ({
    isActive: state.checkout.activeStep === ownProps.id,
    isComplete: state.checkout.completedSteps.indexOf(ownProps.id) > -1,
  }),
  (dispatch, { id }): IOwnDispatchProps => ({
    complete: () => dispatch(completeStep({ id })),
    register: () => dispatch(registerStep({ id })),
    setActive: () => dispatch(setActiveStep({ id })),
  }),
)(CheckoutStep);

export default ConnectedCheckoutStep;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Checkout/CheckoutStep.tsx